/**
 * Normalize UniversalChat / MetaWhatsApp history attachments for ConversationView.
 * Mirrors VueStandaloneAgent normalizeWhatsAppHistoryAttachment (lighter, no Meta allow-list).
 */

const AUDIO_EXT = new Set(['aac', 'amr', 'mp3', 'm4a', 'ogg', 'opus', 'wav', 'webm']);
const VIDEO_EXT = new Set(['mp4', '3gp', '3gpp', 'mov', 'webm']);
const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp']);

function getExtension(name) {
    const base = String(name || '').split(/[/\\]/).pop() || '';
    const i = base.lastIndexOf('.');
    return i >= 0 ? base.slice(i + 1).toLowerCase() : '';
}

function normalizeMime(value) {
    return String(value || '').toLowerCase().trim().split(';')[0].trim();
}

export function resolveConversationMediaUrl(url, apiBase = '') {
    const value = String(url || '').trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
        return value;
    }
    const base = String(apiBase || '').replace(/\/$/, '');
    if (!base) return value;
    return value.startsWith('/') ? `${base}${value}` : `${base}/${value}`;
}

/** Plain caption/body text for UniversalChat / WhatsApp history rows. */
export function getMessagePlainText(message) {
    const candidates = [
        message?.content?.Message?.Text,
        message?.content?.Message?.text,
        message?.content?.Message?.Body,
        message?.content?.Message?.body,
        message?.content?.text,
        message?.Text,
        message?.text,
        message?.Body,
        message?.body,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
        const value = candidates[i];
        if (value == null) continue;
        const trimmed = String(value).trim();
        if (trimmed) return trimmed;
    }
    return '';
}

/**
 * @returns {{
 *   kind: 'audio'|'video'|'image'|'document'|'unsupported',
 *   fileName: string,
 *   url: string,
 *   isVoice: boolean,
 * }[]}
 */
export function getMessageAttachments(message, apiBase = '') {
    const raw =
        message?.content?.Message?.Attachments
        || message?.content?.Message?.attachments
        || message?.content?.Attachments
        || message?.Attachments
        || message?.attachments
        || [];
    if (!Array.isArray(raw) || !raw.length) return [];

    return raw.map((attachment) => {
        if (!attachment || typeof attachment !== 'object') {
            return { kind: 'unsupported', fileName: '', url: '', isVoice: false };
        }

        const rawName = String(
            attachment.file
            || attachment.OriginalFileName
            || attachment.originalFileName
            || attachment.ServerFileName
            || attachment.fileName
            || '',
        );
        const fileName = rawName.includes('/') || rawName.includes('\\')
            ? rawName.split(/[/\\]/).pop()
            : rawName;
        const url = resolveConversationMediaUrl(
            attachment.url || attachment.URL || attachment.payload?.url || '',
            apiBase,
        );
        const typeToken = String(attachment.type || '').toLowerCase().trim();
        const mime = normalizeMime(
            attachment.ContentType
            || attachment.contentType
            || (typeToken.includes('/') ? typeToken : ''),
        );
        const fileFormat = String(attachment.fileFormat || getExtension(fileName) || '').toLowerCase();

        let kind = 'unsupported';
        if (
            typeToken === 'audio'
            || typeToken.startsWith('audio')
            || mime.startsWith('audio/')
            || AUDIO_EXT.has(fileFormat)
        ) {
            kind = 'audio';
        } else if (
            typeToken === 'video'
            || typeToken.startsWith('video')
            || mime.startsWith('video/')
            || VIDEO_EXT.has(fileFormat)
        ) {
            kind = 'video';
        } else if (
            typeToken === 'image'
            || typeToken.startsWith('image')
            || mime.startsWith('image/')
            || IMAGE_EXT.has(fileFormat)
        ) {
            kind = 'image';
        } else if (url) {
            kind = 'document';
        }

        const isVoice = !!(
            attachment.IsVoice === true
            || attachment.IsVoice === 'true'
            || attachment.isVoice === true
            || /whatsapp-voice/i.test(fileName)
            || (kind === 'audio' && (fileFormat === 'ogg' || mime === 'audio/ogg'))
        );

        return { kind, fileName, url, isVoice };
    }).filter((a) => a.url || a.kind !== 'unsupported');
}
